// SYSTEM_ROUTE: /health
import { useState } from 'react';
import type { HealthLog, HealthSummary, ReadinessBand } from '@life-os/app-core';
import { readinessBand } from '@life-os/app-core';
import { Button, Chip, CompactRow, EmptyState, Pane, Stack } from '@life-os/ui';
import type { ChipTone } from '@life-os/ui';
import { useServices, useStore } from '../app/store.js';
import { useAsync } from '../lib/useAsync.js';
import { ScreenHeader } from '../app/ScreenHeader.js';

const BAND_TONE: Record<ReadinessBand, ChipTone> = {
  rest: 'warn',
  steady: 'neutral',
  strong: 'primary',
};

const BAND_LABEL: Record<ReadinessBand, string> = {
  rest: 'Rest & recover',
  steady: 'Steady',
  strong: 'Strong day',
};

const inputStyle = {
  background: 'var(--lc-surface-container)',
  color: 'var(--lc-on-surface)',
  border: '1px solid var(--lc-outline-variant)',
  borderRadius: 'var(--lc-radius-full)',
  padding: '0 12px',
  height: 34,
  font: 'inherit',
  fontSize: 'var(--lc-text-sm)',
} as const;

function weekRange(): { from: string; to: string } {
  const now = new Date();
  const from = new Date(now);
  from.setUTCDate(from.getUTCDate() - 6);
  return { from: from.toISOString().slice(0, 10), to: now.toISOString().slice(0, 10) };
}

export function HealthScreen() {
  const services = useServices();
  const { revision, refresh } = useStore();

  const [sleep, setSleep] = useState(7.5);
  const [energy, setEnergy] = useState(3);
  const [movement, setMovement] = useState(0);
  const [note, setNote] = useState('');

  const logs = useAsync<readonly HealthLog[]>(() => services.health.list(), [revision]);
  const { from, to } = weekRange();
  const summary = useAsync<HealthSummary>(() => services.health.summary(from, to), [revision]);

  const band = readinessBand(sleep, energy);

  const save = async () => {
    await services.health.log({ sleepHours: sleep, energy, movementMinutes: movement, note });
    setNote('');
    setMovement(0);
    refresh();
  };

  const recent = [...(logs.value ?? [])].sort((a, b) => b.occurredOn.localeCompare(a.occurredOn)).slice(0, 14);
  const s = summary.value;

  return (
    <>
      <ScreenHeader title={<>Health & Body</>} subtitle={<>A 20-second daily check-in for sleep, energy, and movement. These are wellness signals to guide
            your day — not medical truth, not a score.</>} actions={<><Chip tone={BAND_TONE[band]}>{BAND_LABEL[band]}</Chip></>} />

      <Pane title="Today's check-in" hint="How's the body today?">
        <Stack>
          <label style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 'var(--lc-text-sm)' }}>
            <span style={{ width: 90, color: 'var(--lc-on-surface-variant)' }}>Sleep</span>
            <input
              type="range"
              min={0}
              max={12}
              step={0.5}
              value={sleep}
              onChange={(e) => setSleep(Number(e.target.value))}
              aria-label="Sleep hours"
              style={{ flex: 1 }}
            />
            <span style={{ width: 56, textAlign: 'right' }}>{sleep} h</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 'var(--lc-text-sm)' }}>
            <span style={{ width: 90, color: 'var(--lc-on-surface-variant)' }}>Energy</span>
            <input
              type="range"
              min={1}
              max={5}
              step={1}
              value={energy}
              onChange={(e) => setEnergy(Number(e.target.value))}
              aria-label="Energy level"
              style={{ flex: 1 }}
            />
            <span style={{ width: 56, textAlign: 'right' }}>{energy}/5</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: 'var(--lc-text-sm)' }}>
            <span style={{ width: 90, color: 'var(--lc-on-surface-variant)' }}>Movement</span>
            <input
              type="number"
              min={0}
              step={5}
              value={movement}
              onChange={(e) => setMovement(Math.max(0, Number(e.target.value) || 0))}
              aria-label="Movement minutes"
              style={{ ...inputStyle, width: 90 }}
            />
            <span style={{ color: 'var(--lc-on-surface-variant)' }}>min</span>
          </label>
          <div style={{ display: 'flex', gap: 'var(--lc-stack-sm)' }}>
            <input
              className="lc-cmdbar"
              style={{ flex: 1 }}
              value={note}
              placeholder="Optional note (how you feel)…"
              onChange={(e) => setNote(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void save();
              }}
              aria-label="Note"
            />
            <Button variant="primary" onClick={() => void save()}>
              Log today
            </Button>
          </div>
        </Stack>
      </Pane>

      {s && s.daysLogged > 0 ? (
        <Pane title="This week" hint={`${s.daysLogged} day${s.daysLogged === 1 ? '' : 's'} logged`}>
          <div style={{ display: 'flex', gap: 'var(--lc-stack-md)', flexWrap: 'wrap' }}>
            <Chip tone="neutral">Avg sleep {s.avgSleepHours} h</Chip>
            <Chip tone="neutral">Avg energy {s.avgEnergy}/5</Chip>
            <Chip tone="primary">{s.totalMovementMinutes} min moving</Chip>
          </div>
        </Pane>
      ) : null}

      <Pane title="Recent" hint={`${recent.length}`}>
        <Stack>
          {recent.length === 0 ? (
            <EmptyState title="No check-ins yet">A quick daily reading builds a calm picture of your energy over time.</EmptyState>
          ) : (
            recent.map((l) => {
              const b = readinessBand(l.sleepHours, l.energy);
              return (
                <CompactRow
                  key={String(l.id)}
                  title={l.occurredOn.slice(0, 10)}
                  subtitle={`${l.sleepHours}h sleep · energy ${l.energy}/5 · ${l.movementMinutes}min${l.note ? ` · ${l.note}` : ''}`}
                  meta={<Chip tone={BAND_TONE[b]}>{BAND_LABEL[b]}</Chip>}
                />
              );
            })
          )}
        </Stack>
      </Pane>
    </>
  );
}
